/**
 * ProtectedRoutes.tsx
 *
 * This component handles route protection based on user permissions, subscriptions, and roles.
 * It determines what content a user can access based on their session information and redirects
 * users without proper access to a no-access page.
 */

import { shallowEqual, useSelector } from "react-redux";
import { getProduct, selectSessionUIFields } from "../../app/sessionSlice";
import {
    useIsCEMRole,
    useIsIAM,
    useIsInternalUser,
    useIsManagedServiceUser,
    useIsNoAccessUser,
    useIsRetailOnlyUser,
} from "../../hooks/useAccessControl";
import { useAccessible } from "../../hooks/useAccessible";
import { matchSubscriptions } from "../../shared/Utils";
import NoAccessErrorPage from "../errorHandling/NoAccessErrorPage";
import { EcmRole, Subscription } from "./Subscription";
import type { ProtectedProps } from "./types";

/**
 * Protected Route Component
 *
 * Wraps child components with access control logic to ensure users only access
 * content they have permission to view based on their subscription, role, and permissions.
 */
const Protected = ({ children, accessConfig }: ProtectedProps) => {
    // Redux state and hooks
    const session = useSelector(selectSessionUIFields, shallowEqual);
    const { hasIAMAccess } = useAccessible();
    const subscriptionData = useSelector(getProduct, shallowEqual) as string[] | undefined;
    const isInternalUser = useIsInternalUser();
    let currentSubscription: string[] = Array.isArray(subscriptionData)
        ? (subscriptionData as string[])
        : [];

    // Access control flags
    const isNoAccessUserFlag = useIsNoAccessUser();
    const isCEMRole = useIsCEMRole();
    const isIAM = useIsIAM();
    const isMSUser = useIsManagedServiceUser();
    const isRetailOnlyUser = useIsRetailOnlyUser();

    const isPermitted = (): boolean => {
        // Extract access configuration
        const { allowedSubscriptions, allowedCEMRoles, allowedRETRoles, internalUser } =
            accessConfig || {};

        if (internalUser && !isInternalUser) {
            return false;
        }

        // Fallback to the lowest tier if no valid subscription found
        if (!matchSubscriptions(Object.values(Subscription), currentSubscription)) {
            currentSubscription = [Subscription.AvaTaxExemptions];
        }

        // Common subscription validation
        const subscriptionsAllowed = allowedSubscriptions
            ? matchSubscriptions(allowedSubscriptions, currentSubscription)
            : true;

        // IAM-specific logic
        if (isIAM) {
            const iamPermitted = accessConfig ? hasIAMAccess(accessConfig) : true;
            return Boolean(subscriptionsAllowed && iamPermitted);
        }

        // Non-IAM specific logic
        // Check for escape MS user
        if (isMSUser && allowedCEMRoles?.includes(EcmRole.MSUser)) {
            return true;
        }

        const cemRole =
            !isCEMRole ||
            !allowedCEMRoles ||
            matchSubscriptions(allowedCEMRoles, session.ecmRole || []);
        const retRole =
            !isRetailOnlyUser ||
            (allowedRETRoles && matchSubscriptions(allowedRETRoles, session.ecmRole || []));
        return Boolean(subscriptionsAllowed && cemRole && retRole);
    };

    if (isNoAccessUserFlag || !isPermitted()) {
        return <NoAccessErrorPage type="404" />;
    }

    return <>{children}</>;
};

export default Protected;
